/**
 * FormField Component
 *
 * Labeled input field with optional tooltip.
 *
 * @package PresszoneComments
 */

import { el } from '../utils/dom.js';
import tooltipManager from './Tooltip.js';

/**
 * Render a text/number input field.
 *
 * @param {string} key - State key
 * @param {string} label - Field label
 * @param {string} help - Tooltip help text
 * @param {string} type - Input type (text, number, etc.)
 * @param {Object} state - State object containing value
 * @param {Function} onUpdate - Callback when value changes
 * @param {Object} props - Additional properties (styling, padding, attrs, etc.)
 */
export function FormField(key, label, help, type, state, onUpdate, props = {}) {
    const { styling, padding, showBorders = true, style, ...inputProps } = props;

    const classes = ['presszone-comments-input--settings'];
    if (styling) classes.push(`presszone-comments-input--styling-${styling}`);
    if (padding) classes.push(`presszone-comments-input--padding-${padding}`);
    if (showBorders === false) classes.push('presszone-comments-input--no-borders');

    return el('div', { class: 'presszone-comments-form-group', style: style },
        el('label', { class: 'presszone-comments-label--settings', for: key },
            label,
            help ? el('button', {
                type: 'button',
                class: 'presszone-comments-tooltip-trigger',
                'aria-label': `More information about ${label}`,
                onmouseenter: (e) => {
                    if (!('ontouchstart' in window)) {  // Desktop only
                        tooltipManager.show(e.target, help);
                    }
                },
                onmouseleave: (e) => {
                    if (!('ontouchstart' in window)) {  // Desktop only
                        tooltipManager.hide();
                    }
                },
                onclick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Mobile: toggle tooltip on click
                    if ('ontouchstart' in window) {
                        if (tooltipManager.activeTooltip === e.target) {
                            tooltipManager.hide();
                        } else {
                            tooltipManager.show(e.target, help);
                        }
                    }
                }
            }, 'ⓘ') : null
        ),
        el('input', {
            type,
            id: key,
            class: classes.join(' '),
            value: state[key] !== undefined && state[key] !== null ? state[key] : '',
            onchange: (e) => {
                const value = type === 'number' ? parseInt(e.target.value, 10) : e.target.value;
                state[key] = value;
                if (onUpdate) onUpdate(key, value);
            },
            ...inputProps
        })
    );
}

export default FormField;
